import { getRequest } from "../../utils/requests";
import api from "../../utils/api";
import config from "../../config.json";
import moment from "moment";
import { buildPeriodRange } from "../../utils/period";
import { getManagersCallsStatistics, handleSipuniApiError } from "./utils/sipuniApi";

const BASE = (process.env.REACT_APP_API_BASE || `${config.service}/api`).replace(/\/$/, "");

export async function getKeplerRobMetrics({ from, to, include, period } = {}) {
  try {
    // Заменяем старый /metrics на /kpi с period или from/to + normalize=true
    const params = new URLSearchParams();
    if (period) {
      params.append('period', period);
    } else if (from && to) {
      params.append('from', from);
      params.append('to', to);
      params.append('normalize', 'true');
    } else {
      // По умолчанию берём текущий месяц
      params.append('period', 'month');
    }
    const url = `${BASE}/kepler-rob/kpi?${params.toString()}`;
    const { data } = await getRequest(url);

    const currency = data?.currency || "KZT";
    const totalPlan = Number(data?.total?.plan ?? data?.totalPlan ?? 0);
    const totalFact = Number(data?.total?.fact ?? data?.totalFact ?? 0);
    const totalPercent = data?.total?.percent != null ? Number(data.total.percent) : undefined;

    const managers = Array.isArray(data?.managers)
      ? data.managers.map((m) => ({
        id: m.id,
        name: m.name,
        plan: Number(m?.plan || 0),
        fact: Number(m?.fact ?? m?.salesSum ?? m?.kpi?.salesSum?.fact ?? 0),
        percent: m.percent != null ? Number(m.percent) : undefined,
        kpi: m.kpi || undefined,
      }))
      : [];

    const measurements = data?.measurements
      ? {
        total: {
          plan: Number(data.measurements?.total?.plan || 0),
          fact: Number(data.measurements?.total?.fact || 0),
          percent: Number(data.measurements?.total?.percent || 0),
        },
        managers: Array.isArray(data.measurements?.managers)
          ? data.measurements.managers.map((m) => ({
            id: m.id,
            name: m.name,
            plan: Number(m.plan || 0),
            fact: Number(m.fact || 0),
            percent: Number(m.percent || 0),
          }))
          : [],
      }
      : undefined;

    return { currency, period: { from, to }, totalPlan, totalFact, totalPercent, managers, measurements, isMock: false };
  } catch (e) {
    return {
      currency: "KZT",
      period: { from, to },
      totalPlan: 0,
      totalFact: 0,
      managers: [],
      isMock: true,
    };
  }
}

export async function saveManagerPlans({ period, plans }) {
  // ensure integers and proper month boundaries in UTC are sent from caller
  const from = period?.from;
  const month = typeof from === 'string' ? from.slice(0, 7) : undefined; // YYYY-MM
  const normalizedPlans = (plans || []).map((p) => ({
    managerId: p.managerId,
    userId: p.managerId, // совместимость, если бэк ждёт userId
    plan: Number(p.plan || 0),
    leadsPlan: p.leadsPlan != null ? Number(p.leadsPlan) : undefined, // новый план по лидам (если поддержит бэк)
  }));
  const payload = { period, month, plans: normalizedPlans };
  console.debug('saveManagerPlans payload', payload);
  return api.post(`/kepler-rob/plans/manager`, payload);
}

export async function saveTotalPlan({ period, totalPlan, targetCompanyIds = [] }) {
  const from = period?.from;
  const month = typeof from === 'string' ? from.slice(0, 7) : undefined;
  const basePayload = { period, month, totalPlan: Number(totalPlan || 0) };
  if (Array.isArray(targetCompanyIds) && targetCompanyIds.length > 0) {
    const calls = targetCompanyIds.map((companyId) =>
      api.post(`/kepler-rob/plans/total`, { ...basePayload, targetCompanyId: companyId })
    );
    return Promise.all(calls);
  }
  return api.post(`/kepler-rob/plans/total`, basePayload);
}

export async function getAllUsers() {
  const url = `${config.service}/api/user`;
  const resp = await getRequest(url);
  // Existing users API returns { data: { users: [...] } }
  const users = resp?.data?.users || resp?.data || [];
  return users.map((u) => ({ id: u.id || u._id, name: u.fio || u.name || u.email || "Без имени" }));
}

// Dashboard (placeholder) API per new contract
// Временно удалено: неиспользуемые/нереализованные эндпоинты дешборда

// export async function getDashboardPlan() {}
// export async function getDashboardFunnel() {}

// export async function getDashboardManagers() {}

export async function getMe() {
  try {
    const { data } = await getRequest(`${config.service}/api/me`);
    return data;
  } catch (e) {
    return { role: "user" };
  }
}

// Agreements search/mapping
export async function getAgreementByNumber(agreementNumber) {
  const { data } = await api.get(`/integrations/agreements/by-number/${encodeURIComponent(agreementNumber)}`);
  return data;
}

// Manager detailed metrics
export async function getManagerMetrics(managerId, { from, to, include } = {}) {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);
  if (include) params.append('include', include);
  const { data } = await getRequest(`${BASE}/kepler-rob/manager/${encodeURIComponent(managerId)}/metrics?${params.toString()}`);
  return data;
}

// Plans endpoints (overview tab: load/save plans in one call if бек поддержит)
export async function getPlans(month) {
  const { data } = await getRequest(`${BASE}/kepler-rob/plans?month=${encodeURIComponent(month)}`);
  return data;
}

export async function putPlans(month, body) {
  const payload = { month, ...body };
  const { data } = await api.put(`/kepler-rob/plans?month=${encodeURIComponent(month)}`, payload);
  return data;
}

export async function mapAgreementNumber({ amoLeadId, agreementNumber }) {
  const { data } = await api.post(`/integrations/agreements/map`, { amoLeadId, agreementNumber });
  return data;
}

export async function getAgreementsAudit({ from, to }) {
  const { data } = await api.get(`/integrations/agreements/audit`, { params: { from, to } });
  return data;
}

// KPI агрегаты за период: по менеджерам отдаёт факты за from..to
export async function getKpi({ from, to, period, normalize, includeCalls = true } = {}) {
  try {
    // Fallback: если ни period, ни from/to не переданы, берём текущий месяц (UTC ISO) и normalize=true
    if (!period && (!from || !to)) {
      try {
        const d = buildPeriodRange({ type: 'month' });
        from = d.fromUTC;
        to = d.toUTC;
        normalize = true;
        console.warn('[getKpi] Fallback to month range because params are empty', { from, to, normalize });
      } catch (e) {
        console.warn('[getKpi] Failed to build fallback period, proceeding without', e);
      }
    }

    const params = new URLSearchParams();
    if (period) {
      params.append('period', period);
    } else {
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      if (normalize) params.append('normalize', 'true');
    }

    // Загружаем основные KPI данные
    const url = `${BASE}/kepler-rob/kpi?${params.toString()}`;

    let { data } = await getRequest(url);

    // Fallback: если период не поддержан на бэке и вернулись пустые данные — пробуем from/to + normalize
    let usedFallbackRange = null;
    if (period && (!data || !Array.isArray(data.managers) || data.managers.length === 0)) {
      try {
        const mapKnown = new Set(['today', 'yesterday', 'week', 'month']);
        const rangeType = mapKnown.has(period) ? period : 'custom';
        const computed = rangeType === 'custom' ? null : buildPeriodRange({ type: rangeType });
        if (computed) {
          const fallbackParams = new URLSearchParams();
          fallbackParams.append('from', computed.fromUTC);
          fallbackParams.append('to', computed.toUTC);
          fallbackParams.append('normalize', 'true');
          const fallbackUrl = `${BASE}/kepler-rob/kpi?${fallbackParams.toString()}`;
          console.warn('↩️ [DEBUG] getKpi fallback by range', { fallbackUrl, computed });
          const resp = await getRequest(fallbackUrl);
          data = resp?.data;
          usedFallbackRange = computed;
        }
      } catch (e) {
        console.warn('⚠️ getKpi fallback failed', e);
      }
    }

    // Детальное логирование ответа API, особенно для коротких периодов
    const isShortPeriod = from && to && moment.utc(to).diff(moment.utc(from), 'days') <= 1;
    if (isShortPeriod || !data?.managers || data.managers.length === 0) {
      /* debug removed */
    }

    // Нормализуем менеджеров: гарантируем плоские факт-поля, даже если бек отдаёт их внутри manager.kpi.*
    const managers = Array.isArray(data?.managers)
      ? data.managers.map((m) => ({
        ...m,
        leads: Number(m?.leads ?? m?.kpi?.leads?.fact ?? 0),
        qualified: Number(m?.qualified ?? m?.kpi?.qualified?.fact ?? 0),
        measure: Number(m?.measure ?? m?.kpi?.measure?.fact ?? 0),
        salesCount: Number(m?.salesCount ?? m?.kpi?.salesCount?.fact ?? 0),
        salesSum: Number(m?.salesSum ?? m?.kpi?.salesSum?.fact ?? 0),
        avgCheck: Number(m?.avgCheck ?? m?.kpi?.avgCheck?.fact ?? 0),
      }))
      : [];

    // Загружаем данные о звонках из Sipuni только если includeCalls = true
    let callsData = { managers: [] };
    if (includeCalls) {
      try {
        if (usedFallbackRange) {
          callsData = await getManagersCallsStatistics(usedFallbackRange.fromUTC, usedFallbackRange.toUTC, { normalize: true });
        } else if (period) {
          callsData = await getManagersCallsStatistics(undefined, undefined, { period });
        } else {
          callsData = await getManagersCallsStatistics(from, to, { normalize });
        }
        // Убеждаемся, что managers существует и является массивом
        if (!callsData || !Array.isArray(callsData.managers)) {
          callsData = { managers: [] };
        }
      } catch (callsError) {
        console.warn('Не удалось загрузить данные о звонках:', callsError);
        handleSipuniApiError(callsError);
        callsData = { managers: [] };
      }
    }

    // Объединяем данные KPI с данными о звонках
    const normalizeStr = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : '');
    const callsArray = Array.isArray(callsData.managers) ? callsData.managers : [];



    const managersWithCalls = managers.map(manager => {
      // Убедимся что manager.id - строка
      const managerIdStr = String(manager.id || manager._id || '');
      const managerId = manager.id || manager._id;

      // 1) По id (строка vs строка)
      let callsStats = callsArray.find(c => {
        const cId = String(c.id || c.userId || '');
        return cId === managerIdStr || cId === String(managerId);
      });

      // 2) По userId менеджера
      if (!callsStats && manager.userId) {
        const userIdStr = String(manager.userId);
        callsStats = callsArray.find(c => {
          const cId = String(c.id || c.userId || '');
          return cId === userIdStr;
        });
      }

      // 3) По имени (кейсы когда id не совпадают, но имена совпадают)
      if (!callsStats && manager.name) {
        const mName = normalizeStr(manager.name);
        callsStats = callsArray.find(c => normalizeStr(c.name) === mName);
      }

      // Логируем несовпадения только если есть данные в Sipuni, но не найдены
      if (!callsStats && callsArray.length > 0 && manager.name) {
        console.debug('[DEBUG] MISMATCH_ID:', {
          name: manager.name,
          kpiId: managerIdStr,
          kpiIdType: typeof managerId,
          sipuniAvailableIds: callsArray.slice(0, 3).map(c => ({ id: String(c.id || ''), name: c.name }))
        });
      }

      callsStats = callsStats || {};

      // Извлекаем данные о звонках из Sipuni формата
      const sipuniCalls = callsStats.calls || {};

      return {
        ...manager,
        calls: {
          incomingMinutes: sipuniCalls.incomingMinutes || callsStats.incomingMinutes || 0,
          outgoingMinutes: sipuniCalls.outgoingMinutes || callsStats.outgoingMinutes || 0,
          totalMinutes: sipuniCalls.totalMinutes || (sipuniCalls.incomingMinutes || 0) + (sipuniCalls.outgoingMinutes || 0) || 0
        },
        callsIncoming: sipuniCalls.incomingCalls || callsStats.incomingCalls || 0,
        callsOutgoing: sipuniCalls.outgoingCalls || callsStats.outgoingCalls || 0,
        callsTotal: sipuniCalls.totalCalls || callsStats.totalCalls || 0,
        callsAnswered: sipuniCalls.answeredCalls || callsStats.answeredCalls || 0,
        callsMissed: sipuniCalls.missedCalls || callsStats.missedCalls || 0,
        averageCallDuration: sipuniCalls.averageMinutes || callsStats.averageMinutes || 0
      };
    });

    // Проверяем результат объединения
    const mergedWithCalls = managersWithCalls.filter(m =>
      m.calls?.totalMinutes > 0 ||
      m.callsIncoming > 0 ||
      m.callsOutgoing > 0
    );



    // ожидаемый формат: { managers: [{ id, name, leads, qualified, measure, salesCount, salesSum, callsIncoming, callsOutgoing }] }
    return { managers: managersWithCalls };
  } catch (e) {
    console.error('❌ Ошибка загрузки KPI данных:', { from, to, error: e });
    return { managers: [] };
  }
}

// ============ Ручное редактирование фактов ============

/**
 * Сохранение ручной корректировки фактов менеджера за конкретную дату
 * @param {Object} params
 * @param {string} params.managerId - ID менеджера
 * @param {string} params.date - дата в формате YYYY-MM-DD
 * @param {Object} params.facts - объект с фактами { leads, qualified, measure, salesCount, salesSum, avgCheck }
 * @returns {Promise}
 */
export async function saveManualFacts({ managerId, date, facts }) {
  const payload = {
    managerId,
    userId: managerId, // совместимость с бэком, если ожидает поле userId
    date,
    facts,
  };
  console.debug('saveManualFacts payload:', payload);
  try {
    const { data } = await api.post(`/kepler-rob/facts/manual`, payload);
    return data;
  } catch (err) {
    const backendMessage = err?.response?.data?.message || err?.response?.data?.error;
    if (backendMessage) {
      console.error('API error', { url: '/kepler-rob/facts/manual', method: 'post', status: err?.response?.status, data: err?.response?.data });
      throw new Error(backendMessage);
    }
    throw err;
  }
}

/**
 * Получение фактов с флагом источника (автоматические или ручные)
 * @param {Object} params
 * @param {string} params.managerId - ID менеджера
 * @param {string} params.from - дата начала в формате YYYY-MM-DD
 * @param {string} params.to - дата окончания в формате YYYY-MM-DD
 * @returns {Promise<Object>} - объект с фактами и флагами isManual
 */
export async function getFactsWithSource({ managerId, from, to }) {
  try {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);

    const { data } = await getRequest(
      `${BASE}/kepler-rob/facts/manual/${encodeURIComponent(managerId)}?${params.toString()}`
    );
    return data;
  } catch (e) {
    console.error('Ошибка получения фактов с источником:', e);
    return null;
  }
}

/**
 * Получение истории изменений фактов менеджера
 * @param {string} managerId - ID менеджера
 * @param {string} from - дата начала в формате YYYY-MM-DD
 * @param {string} to - дата окончания в формате YYYY-MM-DD
 * @returns {Promise<Array>} - массив записей истории
 */
export async function getFactsHistory(managerId, from, to) {
  try {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);

    const { data } = await getRequest(
      `${BASE}/kepler-rob/facts/history/${encodeURIComponent(managerId)}?${params.toString()}`
    );
    return Array.isArray(data) ? data : data?.history || [];
  } catch (e) {
    console.error('Ошибка получения истории фактов:', e);
    return [];
  }
}

export async function getFactsForDate(managerId, date) {
  try {
    const from = date;
    const to = date;
    const data = await getFactsWithSource({ managerId, from, to });
    // ожидаемый ответ: { facts: { leads, qualified, measure, salesCount, salesSum, ... } } или массив записей
    if (!data) return null;
    if (Array.isArray(data)) {
      // если массив, берём первую запись
      return data[0]?.facts || null;
    }
    if (data?.facts) return data.facts;
    if (data?.managers && Array.isArray(data.managers)) {
      const found = data.managers.find(m => m.id === managerId || m.managerId === managerId);
      return found?.facts || null;
    }
    return null;
  } catch (e) {
    console.error('Ошибка получения фактов за дату:', e);
    return null;
  }
}

/**
 * Получение данных для ежедневного отчета (одним запросом)
 */
export async function getDailyReport({ from, to }) {
  const params = new URLSearchParams();
  if (from) params.append('from', from);
  if (to) params.append('to', to);

  const url = `${BASE}/kepler-rob/reports/daily?${params.toString()}`;
  const { data } = await getRequest(url);
  return data?.data;
}
